class SupportCenter extends HTMLElement {
    constructor() {
      super();
      this.searchInput = this.querySelector('[data-support-search]');
      this.status = this.querySelector('[data-support-status]');
      this.products = Array.from(this.querySelectorAll('[data-support-product]'));
      this.topicViewport = this.querySelector('[data-support-topic-viewport]');
      this.topicTrack = this.querySelector('[data-support-topic-track]');
      this.topicControls = this.querySelector('[data-support-topic-controls]');
      this.topicPrevious = this.querySelector('[data-support-topic-previous]');
      this.topicNext = this.querySelector('[data-support-topic-next]');
      this.onSearch = this.onSearch.bind(this);
      this.onModelLink = this.onModelLink.bind(this);
      this.onTopicScroll = this.updateTopicControls.bind(this);
      this.onTopicResize = this.updateTopicControls.bind(this);
      this.onPreviousTopic = () => this.scrollTopics(-1);
      this.onNextTopic = () => this.scrollTopics(1);
    }

    connectedCallback() {
      this.addEventListener('click', this.onModelLink);
      if (this.searchInput && this.products.length > 0) {
        this.searchInput.addEventListener('input', this.onSearch);
        this.searchInput.addEventListener('search', this.onSearch);
      }

      if (this.topicViewport && this.topicTrack && this.topicPrevious && this.topicNext) {
        this.topicViewport.addEventListener('scroll', this.onTopicScroll, { passive: true });
        this.topicPrevious.addEventListener('click', this.onPreviousTopic);
        this.topicNext.addEventListener('click', this.onNextTopic);
        window.addEventListener('resize', this.onTopicResize);
        this.classList.add('is-topic-enhanced');
        this.updateTopicControls();
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onModelLink);
      if (this.searchInput) {
        this.searchInput.removeEventListener('input', this.onSearch);
        this.searchInput.removeEventListener('search', this.onSearch);
      }

      if (this.topicViewport && this.topicPrevious && this.topicNext) {
        this.classList.remove('is-topic-enhanced');
        this.topicViewport.removeEventListener('scroll', this.onTopicScroll);
        this.topicPrevious.removeEventListener('click', this.onPreviousTopic);
        this.topicNext.removeEventListener('click', this.onNextTopic);
        window.removeEventListener('resize', this.onTopicResize);
      }
    }

    onModelLink(event) {
      const link = event.target.closest('[data-support-model-link]');
      if (!link || !this.contains(link) || !this.searchInput) return;
      // Reveal the destination before native anchor navigation, even when a
      // previous document search has hidden this model's support card.
      this.searchInput.value = '';
      this.onSearch();
    }

    scrollTopics(direction) {
      const topic = this.topicTrack?.querySelector('.support-topic');
      if (!topic || !this.topicViewport) return;

      const trackStyle = getComputedStyle(this.topicTrack);
      const gap = Number.parseFloat(trackStyle.columnGap) || 0;
      const distance = topic.getBoundingClientRect().width + gap;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.topicViewport.scrollBy({
        left: direction * distance,
        behavior: reducedMotion ? 'auto' : 'smooth'
      });
    }

    updateTopicControls() {
      if (!this.topicViewport || !this.topicControls || !this.topicPrevious || !this.topicNext) return;

      const maxScroll = Math.max(0, this.topicViewport.scrollWidth - this.topicViewport.clientWidth);
      this.topicControls.hidden = maxScroll <= 1;
      this.topicPrevious.disabled = this.topicViewport.scrollLeft <= 1;
      this.topicNext.disabled = this.topicViewport.scrollLeft >= maxScroll - 1;
    }

    onSearch() {
      const query = this.normalize(this.searchInput.value);
      let visibleProducts = 0;

      this.products.forEach((product) => {
        const productMatches = this.normalize(product.dataset.productSearch).includes(query);
        const documents = Array.from(product.querySelectorAll('[data-support-document]'));
        let visibleDocuments = 0;

        documents.forEach((document) => {
          const documentMatches = this.normalize(document.dataset.documentSearch).includes(query);
          const isVisible = query === '' || productMatches || documentMatches;
          document.hidden = !isVisible;
          if (isVisible) visibleDocuments += 1;
        });

        const isVisible = query === '' || productMatches || visibleDocuments > 0;
        product.hidden = !isVisible;
        if (isVisible) visibleProducts += 1;
      });

      this.updateStatus(visibleProducts);
    }

    normalize(value = '') {
      return value.toLocaleLowerCase().trim();
    }

    updateStatus(count) {
      if (!this.status) return;
      if (count === 0) {
        this.status.textContent = this.dataset.resultEmpty;
        return;
      }
      const label = count === 1 ? this.dataset.resultSingular : this.dataset.resultPlural;
      this.status.textContent = `${count} ${label}`;
    }
  }

  if (!customElements.get('support-center')) {
    customElements.define('support-center', SupportCenter);
  }
